'use client';

import {
  MessageSquare,
  PhoneOff,
  MailX,
  Shield,
  BarChart3,
  Rocket,
  Clock,
  Eye,
  TrendingUp,
  Zap,
  FileText,
  Bell,
  Heart,
  DollarSign,
} from 'lucide-react';

export interface Screen {
  heading: string;
  subtitle?: string;
  icon: typeof MessageSquare;
  iconBg: string;
  iconColor: string;
  body: React.ReactNode;
}

export const SCREENS: Screen[] = [
  /* -- 1. Your Clients Prefer Texting -- */
  {
    heading: 'Your Clients Prefer Texting',
    subtitle: 'The data is clear \u2014 SMS is how people want to communicate.',
    icon: MessageSquare,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    body: (
      <>
        <p className="text-[15px]">
          Your clients aren&rsquo;t waiting by the phone or refreshing their inbox.
          They&rsquo;re texting. And the numbers prove it:
        </p>
        <div className="grid gap-2.5 mt-1">
          {[
            {
              stat: '98%',
              label: 'Open Rate',
              desc: 'Nearly every text message is read \u2014 most within 3 minutes',
              color: 'bg-blue-600',
            },
            {
              stat: '45%',
              label: 'Response Rate',
              desc: 'Almost half of all text messages receive a reply',
              color: 'bg-blue-600',
            },
            {
              stat: '79%',
              label: 'Consumer Opt-In',
              desc: 'Of consumers have opted in to receive texts from businesses',
              color: 'bg-blue-600',
            },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3">
              <div className={`flex items-center justify-center h-10 w-10 rounded-lg ${item.color} text-white text-sm font-bold shrink-0`}>
                {item.stat}
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3 italic">
          31% more consumers texted businesses in 2023 than 2022 &mdash; and the trend is accelerating.
        </p>
      </>
    ),
  },

  /* -- 2. Phone Calls Aren't Working -- */
  {
    heading: "Phone Calls Aren\u2019t Working",
    subtitle: 'Your clients stopped answering. Here\u2019s the proof.',
    icon: PhoneOff,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    body: (
      <>
        <p className="text-[15px]">
          Insurance agencies have relied on phone calls for decades. But client behavior
          has fundamentally changed:
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          {[
            {
              icon: PhoneOff,
              stat: '87%',
              label: "of Americans don\u2019t answer calls from unknown numbers",
            },
            {
              icon: Clock,
              stat: '80%',
              label: 'of business calls go straight to voicemail',
            },
            {
              icon: Eye,
              stat: '15%',
              label: 'of voicemails are actually listened to',
            },
            {
              icon: TrendingUp,
              stat: '2\u20133%',
              label: 'average conversion rate from cold calls',
            },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100 shrink-0">
                <span className="text-sm font-bold text-red-600">{item.stat}</span>
              </div>
              <p className="text-sm text-gray-700">{item.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg bg-red-50 border border-red-100 p-3">
          <p className="text-sm text-gray-700">
            <strong>The bottom line:</strong> When you call a client, there&rsquo;s a{' '}
            <strong>less than 20% chance</strong> they pick up. When you text them,
            there&rsquo;s a <strong>98% chance</strong> they read it.
          </p>
        </div>
      </>
    ),
  },

  /* -- 3. Email Is Getting Ignored -- */
  {
    heading: 'Email Is Getting Ignored',
    subtitle: 'Open rates are inflated. Real engagement is declining.',
    icon: MailX,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    body: (
      <>
        <p className="text-[15px]">
          Email used to be reliable. But between spam filters, promotional tabs, and
          inbox overload, your messages are competing with hundreds of others:
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900">SMS vs. Email</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">98%</div>
                <div className="text-xs text-gray-500">SMS Open Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">~20%</div>
                <div className="text-xs text-gray-500">Email Open Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">45%</div>
                <div className="text-xs text-gray-500">SMS Response Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">6%</div>
                <div className="text-xs text-gray-500">Email Response Rate</div>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-amber-100 shrink-0 mt-0.5">
              <Clock size={16} className="text-amber-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">Response Time</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Average email response: <strong>2.6 hours</strong>. Average text response: <strong>90 seconds</strong>.
                When a client needs help, speed matters.
              </p>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-3 italic">
          Note: Apple Mail Privacy Protection artificially inflates email open rates by up to 18
          percentage points &mdash; real engagement is even lower than reported.
        </p>
      </>
    ),
  },

  /* -- 4. SMS for Insurance Agencies -- */
  {
    heading: 'SMS for Insurance Agencies',
    subtitle: 'Every stage of the client lifecycle is better with texting.',
    icon: Shield,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    body: (
      <>
        <p className="text-[15px]">
          SMS isn&rsquo;t just marketing &mdash; it&rsquo;s the fastest way to handle the
          daily communication that drives your agency:
        </p>
        <div className="flex flex-col gap-2.5 mt-1">
          {[
            {
              icon: Bell,
              label: 'Renewal Reminders',
              desc: 'Send timely renewal notices that actually get read. No more missed renewals or lapsed policies.',
            },
            {
              icon: FileText,
              label: 'Claims Updates',
              desc: 'Keep clients informed on claim status instantly. Reduce inbound "where\u2019s my claim?" calls.',
            },
            {
              icon: DollarSign,
              label: 'Payment Reminders',
              desc: 'Gentle payment nudges via text have dramatically higher response rates than email or mail.',
            },
            {
              icon: Zap,
              label: 'Quote Follow-Ups',
              desc: 'Follow up on quotes within minutes. 75% of consumers are more likely to convert when they can message instead of call.',
            },
            {
              icon: Heart,
              label: 'Client Relationships',
              desc: 'Birthday messages, policy anniversaries, check-ins. Small touches that build loyalty and retention.',
            },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-100 shrink-0 mt-0.5">
                <item.icon size={16} className="text-emerald-600" />
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },

  /* -- 5. The Numbers Don't Lie -- */
  {
    heading: "The Numbers Don\u2019t Lie",
    subtitle: 'SMS outperforms every other channel \u2014 by a wide margin.',
    icon: BarChart3,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600',
    body: (
      <>
        <div className="rounded-lg border border-violet-100 bg-violet-50/30 p-4">
          <div className="grid grid-cols-4 gap-2 text-center mb-3">
            <div />
            <div className="text-xs font-semibold text-gray-900">SMS</div>
            <div className="text-xs font-semibold text-gray-900">Email</div>
            <div className="text-xs font-semibold text-gray-900">Phone</div>
          </div>
          {[
            { metric: 'Open / Answer', sms: '98%', email: '~20%', phone: '~20%' },
            { metric: 'Response Rate', sms: '45%', email: '6%', phone: '2\u20133%' },
            { metric: 'Avg Response Time', sms: '90 sec', email: '2.6 hrs', phone: 'N/A' },
            { metric: 'Delivery Rate', sms: '96%', email: '~85%', phone: 'N/A' },
          ].map((row, i) => (
            <div key={row.metric} className={`grid grid-cols-4 gap-2 text-center py-2 ${i > 0 ? 'border-t border-violet-100' : ''}`}>
              <div className="text-xs font-medium text-gray-700 text-left">{row.metric}</div>
              <div className="text-xs font-bold text-blue-600">{row.sms}</div>
              <div className="text-xs text-gray-500">{row.email}</div>
              <div className="text-xs text-gray-500">{row.phone}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-3">
          <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 text-center">
            <div className="text-2xl font-bold text-violet-600">683%</div>
            <p className="text-xs text-gray-500 mt-1">
              more likely to report marketing success when using SMS
            </p>
          </div>
          <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 text-center">
            <div className="text-2xl font-bold text-violet-600">$21&ndash;71</div>
            <p className="text-xs text-gray-500 mt-1">
              return for every $1 spent on SMS marketing
            </p>
          </div>
        </div>
      </>
    ),
  },

  /* -- 6. Start Texting Your Clients -- */
  {
    heading: 'Start Texting Your Clients',
    subtitle: "You\u2019re already set up. Here\u2019s why it matters.",
    icon: Rocket,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    body: (
      <>
        <p className="text-[15px] mb-1">
          Every day you rely solely on phone calls and email, you&rsquo;re missing
          conversations. With Voley, you&rsquo;re already equipped to change that:
        </p>
        <div className="flex flex-col gap-3 mt-1">
          {[
            {
              num: '1',
              title: 'Reach clients where they are',
              desc: '98% of your messages will be read. No more phone tag, no more ignored emails.',
            },
            {
              num: '2',
              title: 'Respond faster than your competition',
              desc: 'The average text gets a response in 90 seconds. Speed wins in insurance.',
            },
            {
              num: '3',
              title: 'Every conversation is documented',
              desc: 'Full audit trail with timestamps, sender names, and AMS logging. E&O protection built in.',
            },
            {
              num: '4',
              title: 'Compliance is automatic',
              desc: 'Consent tracking, opt-out handling, and carrier compliance \u2014 all managed by Voley.',
            },
          ].map((item) => (
            <div key={item.num} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center justify-center h-7 w-7 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">
                {item.num}
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-900">{item.title}</span>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 p-4 text-center">
          <p className="text-sm font-medium text-gray-800">
            &ldquo;The question isn&rsquo;t <em>should</em> your agency text clients &mdash;
            it&rsquo;s <strong>how many opportunities are you losing</strong> by not texting them?&rdquo;
          </p>
        </div>
      </>
    ),
  },
];
